import React from 'react';
import { X, CheckCircle, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ExitIntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  userType?: 'guest' | 'free' | 'paid';
}

const ExitIntentModal: React.FC<ExitIntentModalProps> = ({ 
  isOpen, 
  onClose,
  userType = 'guest'
}) => {
  if (!isOpen) return null;

  const getContent = () => {
    if (userType === 'guest') {
      return {
        title: "Wait! Don't leave yet 👋",
        subtitle: "Create a free account to save your progress",
        benefits: [
          "Save and organize all your clips",
          "1 free video to test (upgrade for unlimited)",
          "Track your content analytics",
          "Access from any device",
          "AI-powered clip detection"
        ],
        ctaText: "Create Free Account",
        ctaLink: "/signup",
        secondaryText: "Already have an account?",
        secondaryLink: "/login",
        secondaryLinkText: "Sign in"
      };
    } else if (userType === 'free') {
      return {
        title: "Before you go...",
        subtitle: "Upgrade to unlock unlimited potential",
        benefits: [
          "Remove watermarks from all clips",
          "Priority processing (2x faster)",
          "Unlimited YouTube downloads",
          "Advanced AI analysis",
          "Up to 50 videos per month"
        ],
        ctaText: "View Pricing Plans",
        ctaLink: "/pricing",
        secondaryText: "Not ready yet?",
        secondaryLink: null,
        secondaryLinkText: "Continue browsing"
      };
    }
    return null;
  };

  const content = getContent();
  if (!content) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-slide-up border border-gray-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-black transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="bg-white px-6 py-8 text-center relative overflow-hidden border-b border-gray-200">
          <div className="absolute inset-0 dot-pattern opacity-30"></div>
          <div className="relative z-10">
            <div className="w-12 h-1 bg-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-display font-bold tracking-tight mb-2 text-black">
              {content.title}
            </h2>
            <p className="text-gray-600 text-sm">
              {content.subtitle}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <ul className="space-y-3 mb-6">
            {content.benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm">{benefit}</span>
              </li>
            ))}
          </ul>

          {/* Value prop */}
          <div className="bg-gray-50 rounded-lg p-3 mb-6 flex items-center justify-center gap-2 border border-gray-200">
            <TrendingUp className="w-4 h-4 text-gray-700" />
            <span className="text-sm text-gray-700">
              <strong className="text-black">Built by creators,</strong> for creators
            </span>
          </div>

          {/* CTA */}
          <Link
            to={content.ctaLink}
            onClick={onClose}
            className="block w-full bg-black hover:bg-gray-900 text-white font-semibold px-6 py-3 rounded-lg transition-all text-center mb-3 active:scale-[0.98]"
          >
            {content.ctaText}
          </Link>

          {/* Secondary action */}
          <div className="text-center text-sm text-gray-600">
            {content.secondaryText}{' '}
            {content.secondaryLink ? (
              <Link
                to={content.secondaryLink}
                onClick={onClose}
                className="text-black hover:text-red-500 font-medium transition-colors"
              >
                {content.secondaryLinkText}
              </Link>
            ) : (
              <button
                onClick={onClose}
                className="text-black hover:text-red-500 font-medium transition-colors"
              >
                {content.secondaryLinkText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentModal;
